import { registerLocaleData } from '@angular/common';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import localeHr from '@angular/common/locales/hr';
import { LOCALE_ID, NgModule, type Type } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  provideAngularQuery,
  QueryClient
} from '@tanstack/angular-query-experimental';

import { ConfigModule } from '@app/config';
import { AppLayoutModule } from '@app/layout/app.layout.module';
import { ConfirmationService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { InplaceModule } from 'primeng/inplace';
import { InputNumberModule } from 'primeng/inputnumber';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SkeletonModule } from 'primeng/skeleton';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToolbarModule } from 'primeng/toolbar';
import { TreeTableModule } from 'primeng/treetable';
import { AppRoutingModule } from './app-routing.module';

import { NTAPCommonModule, NTAPMenuService } from '@ntap/common';
import {
  AttributeStateService,
  CoreConfig,
  InputUtils,
  ItemViewProviderDirective,
  NTAPCoreModule,
  UIComponents,
  UIComponentService
} from '@ntap/core';

import {
  ActionSendFormDataActionComponent,
  ActionServerCloseActionComponent,
  ActivateDeactivateListItemsActionComponent,
  AddObjectToParentActionComponent,
  AppComponent,
  AppGenerirajIzvjestajActionComponent,
  AppIzvjestajCommonItemViewComponent,
  ArhivPosjednikComponent,
  ArhivskaOznakaComponent,
  ChooseFromPersonNormativeDatabaseComponent,
  CommonListGridViewComponent,
  DeduplikacijaAgentDropdownComponent,
  DeleteListUsersActionComponent,
  EarhivUiComponents,
  EditableItemListComponent,
  EditableTableAdditionalActionsComponent,
  EditableTableComponent,
  EditableTableRowComponent,
  FizickiObjektComponent,
  FizickiObjektListaComponent,
  GeonamesIntegrationInput,
  HierarhijaComponent,
  IspisOmotaSpisaActionComponent,
  IzvjestajDownloadService,
  JedinicaGradivaComponent,
  JediniceInventureListComponent,
  KratkaPovijestPromjenaComponent,
  ListToolBarComponent,
  LocateInDokumentacijaTreeActionComponent,
  LockUnlockListItemsActionComponent,
  MainListViewComponent,
  NapomenaIzlucivanjeListaComponent,
  NewFolderActionComponent,
  NewJedinicaGradivaActionComponent,
  NtapListComponent,
  ObustaviRokCuvanjaActionComponent,
  PojavniOblikComponent,
  PotvrdiVrstaZahtjevaActionComponent,
  PovijestPromjenaComponent,
  PredajaActionComponent,
  PretragaKatalogaComponent,
  PretragaKatalogaProviderDirective,
  PublishCloseArhivPosjednikActionComponent,
  RegistarArhivaComponent,
  SaveCloseArhivPosjednikActionComponent,
  SaveCloseDeduplicatedAgentItemActionComponent,
  TableListGridComponent,
  TableListGridProviderDirective,
  ViafIntegrationInput,
  IzradiKlasifikacijskuOznakuComponent,
  SaveOtpremaStatusAction,
  ConfirmIzdavanjeGradivaActionComponent,
  AddParentToKlasaActionComponent,
  KlasaHierarchyListViewProviderDirective,
  KlasaListComponent,
  SaveCloseRefreshDokActionComponent,
  SaveRefreshDokActionComponent,
  SetStatusOtpremeActionComponent,
  NewZahtjevActionComponent,
  PromjenaZaduzenjaZaAgentaOpenItemViewActionComponent,
  EditDokumentacijaDatotekaActionComponent,
  ActivateDeactivateKlasaListItemsActionComponent,
  ObrisiKlasaItemActionComponent,
  CommonItemViewComponent,
  NewPredmetActionComponent,
  NewDokumentActionComponent,
  LockUnlockDokumentacijaListItemsActionComponent,
  NewKlasaActionComponent,
  KonvertirajPojavniOblikActionComponent
} from '@app/components';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DataViewModule } from 'primeng/dataview';
import { DividerModule } from 'primeng/divider';
import { DragDropModule } from 'primeng/dragdrop';
import { TreeModule } from 'primeng/tree';
import { TreeSelectModule } from 'primeng/treeselect';
import { IzlucivanjeActionComponent } from './components/actions/izlucivanje-action/izlucivanje-action.component';
import { NewChildEntityActionComponent } from './components/actions/new-child-entity-action/new-child-entity-action.component';
import { NoviObjektPojavnogActionComponent } from './components/actions/new-fizicki-objekti-action/new-fizicki-objekti-action.component';
import { PosaljiNaVerifikacijuActionComponent } from './components/actions/posalji-na-verifikaciju-action/posalji-na-verifikaciju-action.component';
import { PublishUnpublishJediniceActionComponent } from './components/actions/publish-unpublish-jedinice-action/publish-unpublish-jedinice-action.component';
import { SaveCloseKreiranjeFizickihObjekataActionComponent } from './components/actions/save-close-kreiranje-fizickih-objekata-action/save-close-kreiranje-fizickih-objekata-action.component';
import { AgentPrikazComponent } from './components/agent-prikaz/agent-prikaz.component';
import { AgentComponent } from './components/agent/agent.component';
import { SeupSubjektComponent } from './components/seup-subjekt/seup-subjekt.component';
import IntegrationSearchComponent from './components/geonames/integration-search.component';
import { HierarchyListComponent } from './components/hierarchy-list/hierarchy-list.component';
import { HierarhijaKlasifikacijaComponent } from './components/hierarhija-klasifikacija/hierarhija-klasifikacija.component';
import { HierarhijaPozicijaComponent } from './components/hierarhija-pozicija/hierarhija-pozicija.component';
import { JedinicaGradivaIzlucivanjeListaComponent } from './components/jedinica-gradiva-izlucivanje-lista/jedinica-gradiva-izlucivanje-lista.component';
import { JedinicaGradivaPredajaListaComponent } from './components/jedinica-gradiva-predaja-lista/jedinica-gradiva-predaja-lista.component';
import { MainHierarchylistviewComponent } from './components/main-hierarchylistview/main-hierarchylistview.component';
import { DatotekeListaComponent } from './components/pojavni-oblik/datoteke-lista/datoteke-lista.component';
import { ObjektiPojavnogOblikaComponent } from './components/pojavni-oblik/objekti-pojavnog-oblika/objekti-pojavnog-oblika.component';
import { PriloziRadnogZadatkaComponent } from './components/radni-zadatak/prilozi-radnog-zadatka/prilozi-radnog-zadatka.component';
import { RadniZadatakComponent } from './components/radni-zadatak/radni-zadatak.component';
import {
  HierarchyListViewProviderDirective,
  TreeViewProviderDirective
} from './providers';
import { EarhivInputControlTypes } from './shared/models/earhiv-input-control-types';
import { SharedModule } from './shared/shared.module';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PanelModule } from 'primeng/panel';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ConfirmDostavuFizickihObjekataActionComponent } from './components/actions/action-confirm-dostavu-fizickih-objekata/confirm-dostavu-fizickih-objekata.component';
import { ConfirmReturnFizickiObjektiToSpremisteActionComponent } from './components/actions/action-confirm-return-fizicki-objekti-to-spremiste/confirm-return-fizicki-objekti-to-spremiste.components';
import { AddRelatedItemEntityComponent } from './components/actions/add-related-item-entity/add-related-item-entity.component';
import { NoopViewProviderDirective } from './components/actions/add-related-item-entity/directives/noop-list-view-provider.directive';
import { ConfirmPovratFizickogObjektaActionComponent } from './components/actions/confirm-povrat-gradiva-action/confirm-povrat-gradiva-action.component';
import { DeleteActionComponent } from './components/actions/delete-action/delete-action.component';
import { DeleteItemsListComponent } from './components/actions/delete-items-list/delete-items-list.component';
import { DostavaActionComponent } from './components/actions/dostava-action/dostava-action.component';
import { ObjavaOdobjavaJediniceComponent } from './components/actions/objava-odobjava-jedinice/objava-odobjava-jedinice.component';
import { PotvrdiPremjestanjeActionComponent } from './components/actions/potvrdi-premjestanje-action/potvrdi-premjestanje-action.component';
import { PremjestiActionComponent } from './components/actions/premjesti-action/premjesti-action.component';
import { SaveCloseIzlucivanjeActionComponent } from './components/actions/save-close-izlucivanje-action/save-close-izlucivanje-action.component';
import { PremjestiDialogComponent } from './components/dialogs/premjesti-dialog/premjesti-dialog.component';
import { FastDataEntryEditTemplateBarComponent } from './components/fast-data-entry-input-bar/fast-data-entry-edit-template-bar/fast-data-entry-edit-template-bar.component';
import { FastDataEntryInputBarComponent } from './components/fast-data-entry-input-bar/fast-data-entry-input-bar.component';
import { FastDataEntryComponent } from './components/fast-data-entry/fast-data-entry.component';
import { HierarhijaPosebniPopisComponent } from './components/hierarhija-posebni-popis/hierarhija-posebni-popis.component';
import { AppIzvjestajItemViewComponent } from './components/izvjestaji/components/izvjestaj-item-view.component';
import { JedinicaHierarchyPickerComponent } from './components/jedinica-hierarchy-picker/jedinica-hierarchy-picker.component';
import { LazyDropdownInputControlComponent } from './components/lazy-dropdown-input-control/lazy-dropdown-input-control.component';
import { MojaInstitucijaComponent } from './components/moja-institucija/moja-institucija.component';
import { VrijemeRangeKompleksniComponent } from './components/range-kompleksni/vrijeme-range-kompleksni.component';
import { VrijemeKompleksniComponent } from './components/vrijeme-kompleksni/vrijeme-kompleksni.component';
import { RefreshableAttributeStateService } from './services/refreshable-attribute-state.service';
import { ArhivMenuService } from './services/arhiv-menu.service';

import { BadgeModule } from 'primeng/badge';
import { ListboxModule } from 'primeng/listbox';
import { SplitterModule } from 'primeng/splitter';
import { DeduplicateActionComponent } from './components/actions/deduplicate-action/deduplicate-action.component';
import { EditJedinicaItemActionComponent } from './components/actions/edit-jedinica-item-action/edit-jedinica-item-action.component';
import { ExportAgentDataActionComponent } from './components/actions/export-agent-data-action/export-agent-data-action.component';
import { ExportJediniceGradivaExcelActionComponent } from './components/actions/export-jedinice-gradiva-excel-action/export-jedinice-gradiva-excel-action';
import { ListAndDetailActionComponent } from './components/actions/list-and-detail-common-action/list-and-detail-common-action';
import { PrintLabelPdfActionComponent } from './components/actions/print-label-pdf-action/print-label-pdf-action';
import { PrintLabelSpremistePdfActionComponent } from './components/actions/print-label-spremiste-pdf-action/print-label-spremiste-pdf-action';
import { PrintLabelDostavaPdfActionComponent } from './components/actions/print-label-dostava-pdf-action/print-label-dostava-pdf-action';
import { DodajPovezaneJediniceGradivaIzPostojecihComponent } from './components/dodaj-povezana-jedinica-iz-postojece/dodaj-povezana-jedinica-iz-postojece.component';
import { DokumentacijaHierarhijaComponent } from './components/dokumentacija/dokumentacija-hierarhija.component';
import { EditActionbarComponent } from './components/edit-actionbar/edit-actionbar.component';
import { InMemoryListViewProviderDirective } from './components/editableitemlist.component/services/in-memory-list-view-provider.directive';
import { EntryTemplateDropdownComponent } from './components/entry-template-dropdown/entry-template-dropdown.component';
import { NazivPojavnogOblikaComponent } from './components/inputs/naziv-pojavnog-oblika/naziv-pojavnog-oblika.component';
import { MjestoComponent } from './components/mjesto/mjesto.component';
import { NapomenaPredajaListaComponent } from './components/napomena-preadaja-lista/napomena-predaja-lista.component';
import { ObjektiFizickogObjektaListComponent } from './components/objekti-fizickog-objekta/objekti-fizickog-objekta.component';
import { OpenArhivPosjednikIzJediniceGradivaComponent } from './components/open-arhiv-posjednik-iz-jedinice-gradiva/open-arhiv-posjednik-iz-jedinice-gradiva.component';
import { MojeGradivoComponent } from './components/pretraga-kataloga/moje-gradivo.component';
import { OdabirJediniceZaRestauracijuComponent } from './components/restauracije/odabir-jedinice-za-restauraciju.component';
import { OdabirJediniceZaRestauracijuProviderDirective } from './components/restauracije/odabir-jedinice-za-restauraciju.provider';
import { RokCuvanjaComponent } from './components/rok-cuvanja/rok-cuvanja.component';
import { LocationFromStorageComponent } from './components/spremiste/location-from-storage/location-from-storage.component';
import { ZaglavljeComponent } from './components/spremiste/spremiste zaglavlje/spremiste-zaglavlje.component';
import { SpremisteObjektiViewComponent } from './components/spremiste/spremiste-objekti-view/spremiste-objekti-view.component';
import { SpremisteObjektiComponent } from './components/spremiste/spremiste-objekti/spremiste-objekti.component';
import { SpremisteProstorijeComponent } from './components/spremiste/spremiste-prostorije/spremiste-prostorije.component';
import { PredmetiRestauracijeComponent } from './features/restauracije/predmeti-restauracije/predmeti-restauracije.component';
import { RestauracijaComponent } from './features/restauracije/restauracija/restauracija.component';

import { RouterLink } from '@angular/router';
import { Spremiste2DComponent } from '@app/components/spremiste2D/spremiste2d.component';
import { DialogModule } from 'primeng/dialog';
import { PaginatorModule } from 'primeng/paginator';
import { ScrollerModule } from 'primeng/scroller';
import {
  HomePageComponent,
  HomepageSegmentComponent
} from './admin/components/home-page/home-page.component';
import { DenyAgentVerificationComponent } from './components/actions/deny-agent-verification/deny-agent-verification.component';
import { EditInventuraItemActionComponent } from './components/actions/edit-inventura-item-action/edit-inventura-item-action.component';
import { ExportAgentDrugiZapisiComponent } from './components/actions/export-agent-drugi-zapisi/export-agent-drugi-zapisi.component';
import { MicanjeJediniceIzIstaknutogSadrzajaComponent } from './components/actions/micanje-jedinice-iz-istaknutog-sadrzaja/micanje-jedinice-iz-istaknutog-sadrzaja.component';
import { NijeInventuraItemsListComponent } from './components/actions/nije-inventura.component/nije-inventura-items-list.component';
import { SelectFizickiComponent } from './components/actions/odabir-fizickog-za-restauraciju/odabir-fizickog';
import { OdabirFizickogUSpremisteComponent } from './components/actions/odabir-fizickog-za-restauraciju/odabir-fizickog-u-spremiste.component';
import { OdabirFizickogZaRestauracijuComponent } from './components/actions/odabir-fizickog-za-restauraciju/odabir-fizickog-za-restauraciju.component';
import { OdabirPojavnogZaRestauracijuComponent } from './components/actions/odabir-pojavnog-za-restauraciju/odabir-pojavnog-za-restauraciju.component';
import { SelectPojavniComponent } from './components/actions/odabir-pojavnog-za-restauraciju/odabir-pojavnog.component';
import { PremjestiDokJedinicuActionComponent } from './components/actions/premjesti-dok-jedinicu-action/premjesti-dok-jedinicu.component';
import { PremjestiDokumentActionComponent } from './components/actions/premjesti-dok-jedinicu-action/premjesti-dokument.component';
import { PrintPotvrdaPrimitkaActionComponent } from './components/actions/print-potvrda-primitka-action/print-potvrda-primitka-action.component';
import { RazmjestiNizeActionComponent } from './components/actions/razmjesti-nize-action/razmjesti-nize-action.component';
import { SaveCloseRefreshItemActionComponent } from './components/actions/save-close-refresh-action/save-close-refresh-action.component';
import { Spremiste2DGrafikaActionComponent } from './components/actions/spremiste2-dgrafika-action/spremiste2-dgrafika-action.component';
import { DatotekaComponent } from './components/datoteka/datoteka.component';
import { DodajJediniceGradivaUZahtjevComponent } from './components/dodaj-jedinice-gradiva-u-zahtjev/dodaj-jedinice-gradiva-u-zahtjev.component';
import { GenerirajJedinicaGradivaIzlucivanjeRelActionComponent } from './components/generiraj-jedinica-gradiva-izlucivanje-rel/generiraj-jedinica-gradiva-izlucivanje-rel-action.component';
import { OdabirJediniceZaIstaknutiSadrzajComponent } from './components/istaknuti-sadrzaj/odabir-jedinice-za-istaknuti-sadrzaj.component';
import { OdabirKorisnikaCitaoniceComponent } from './components/korisnik-citaonice/odabir-korisnika-citaonice.component';
import { SelectKorisnikCitaoniceComponent } from './components/korisnik-citaonice/odabir-korisnika.component';
import { OdabirPretrageComponent } from './components/odabir-pretrage/odabir-pretrage.component';
import { PredajaComponent } from './components/predaja/predaja.component';
import { RazmjestiNizeComponent } from './components/razmjesti-nize/razmjesti-nize.component';
import { SelectJedinicaGradivaComponent } from './components/restauracije/odabir-jedinice.component';
import { SaveNoviKorisnikCitaoniceComponent } from './components/save-novi-korisnik-citaonice/save-novi-korisnik-citaonice.component';
import { SpremisteLokacijeComponent } from './components/spremiste/spremiste-lokacije/spremiste-lokacije.component';
import { SpremisteSpremisneJediniceComponent } from './components/spremiste/spremiste-spremisne-jedinice/spremiste-spremisne-jedinice.component';
import { PregledGradivaPoRokuCuvanjaComponent } from './features/izlucivanje/pregled-gradiva-po-roku-cuvanja/pregled-gradiva-po-roku-cuvanja.component';
import { NotificationModule } from './layout/notification.module';
import { SudskiRegistarPanelComponent } from './shared/components/sudski-registar-panel/sudski-registar-panel.component';
import { OibServicePanelComponent } from './shared/components/oib-service-panel/oib-service-panel.component';
import { OibServiceDialogComponent } from './shared/components/oib-service-panel/oib-service-dialog/oib-service-dialog.component';

import { NTAPPrimengObrasciModule } from '@ntap/primeng-obrasci';
import { ButtonModule } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { ActionSaveMjestoComponent } from './components/actions/action-save-mjesto/action-save-mjesto.component';
import { ConfirmationJediniceGradivaComponent } from './components/actions/confirmation-action/confirmation-action-jedinica-gradiva.component';
import { DeleteObrazacUpisaActionComponent } from './components/actions/delete-obrazac-upisa-action/delete-obrazac-upisa-action.component';
import { EditJedinicaGradivaDokComponent } from './components/actions/dokumentacija/edit-jedinica-gradiva-dok';
import { EditObrazacUpisaActionComponent } from './components/actions/edit-obrazac-upisa-action/edit-obrazac-upisa-action.component';
import { BasicItemsListActionComponent } from './components/actions/items-list-action/basic-items-list-action.component';
import { SelectFizickiPredajaComponent } from './components/actions/odabir-fizickog-za-predaju/odabir-fizickog-predaja';
import { OdabirFizickogZaPredajuComponent } from './components/actions/odabir-fizickog-za-predaju/odabir-fizickog-za-predaju.component';
import { SelectPojavniPredajaComponent } from './components/actions/odabir-pojavnog-za-predaju/odabir-pojavnog-predaja.component';
import { OdabirPojavnogZaPredajuComponent } from './components/actions/odabir-pojavnog-za-predaju/odabir-pojavnog-za-predaju.component';
import { OpenPovezanaJedinicaGradivaActionComponent } from './components/actions/open-povezana-jedinica-gradiva-action/open-povezana-jedinica-gradiva-action.component';
import { OpenPovezaniAgentActionComponent } from './components/actions/open-povezani-agent-action/open-povezani-agent-action.component';
import {
  ConfirmEkulturaDialog,
  PosaljiUEkulturuActionComponent
} from './components/actions/posalji-u-ekulturu-action/posalji-u-ekulturu-action.component';
import { PromjenaPoretkaActionComponent } from './components/actions/promjena-poretka-action/promjena-poretka-action.component';
import { PublishUnpublishAgentsComponent } from './components/actions/publish-unpublish-agents/publish-unpublish-agents.component';
import { SaveOpenNewPodrazinaComponent } from './components/actions/save-open-new-podjedinica/save-open-new-podrazina.component';
import { FileUploadProgressToastComponent } from './components/actions/spremi-datoteke-action/file-upload-status';
import { SpremiDatotekeActionComponent } from './components/actions/spremi-datoteke-action/spremi-datoteke-action.component';
import { UkloniFizickiComponent } from './components/actions/ukloni-fizicki-action/ukloni-fizicki-action.component';
import { VerificirajActionComponent } from './components/actions/verificiraj-action/verificiraj-action.component';
import { VerifyAgentsComponent } from './components/actions/verify-agents/verify-agents.component';
import { ZakljucenjePredmetaActionComponent } from './components/actions/zakljucenje-predmeta-action/zakljucenje-predmeta-action.component';
import { ArhivskaKlasifikacijaComponent } from './components/arhivska-klasifikacija/arhivska-klasifikacija.component';
import { FastDataEntryObrasciListProviderDirective } from './components/fast-data-entry/service/fast-data-entry-obrasci-list-provider';
import { EarhivPrimeNGInputMultiSelectComponent } from './components/multiselect-earhiv/earhiv-multiselect.component';
import { SelectAgentComponent } from './components/odabir-agenta/odabir-agenta.component';
import { OdabirPovezanogAgentaComponent } from './components/odabir-agenta/odabir-povezanog-agenta.component';
import { OdabirPovezanogAgentaProviderDirective } from './components/odabir-agenta/odabir-povezanog-agenta.provider';
import { OdabirJediniceZaPredajuComponent } from './components/predaje/odabir-jedinice-za-predaju.component';
import { DownloadActionComponent } from './components/preuzmi-datoteku-dokument/preuzmi-datoteku-dokument.component';
import { OpenFileInNewTabActionComponent } from './components/preuzmi-datoteku-dokument/otvori-datoteku-tab.component';
import { ContentDownloadService } from './components/preuzmi-datoteku-dokument/preuzmi-datoteku-dokument.service';
import { RazmjestiDatotekeIzViseComponent } from './components/razmjesti-iz-vise/razmjesti-datoteke-iz-vise.component';
import { RazmjestiIzViseComponent } from './components/razmjesti-iz-vise/razmjesti-iz-vise.component';
import { BrziObrazacStateServiceProviderDirective } from './directives/brzi-obrazac-state-service-provider.directive';

import { NTAPPrimengModule, PrimengConfigService } from '@ntap/primeng';
import { EditAgentItemActionComponent } from './components/actions/edit-agent-item-action/edit-agent-item-action.component';
import { ExportJediniceGradivaActionComponent } from './components/actions/export-jedinice-gradiva-action/export-jedinice-gradiva-action';
import { SelectFizickiDostavaComponent } from './components/actions/odabir-fizickog-za-dostavu/odabir-fizickog-dostava';
import { OdabirFizickogZaDostavuComponent } from './components/actions/odabir-fizickog-za-dostavu/odabir-fizickog-za-dostavu.component';
import { SelectPojavniDostavaComponent } from './components/actions/odabir-pojavnog-za-dostavu/odabir-pojavnog-dostava.component';
import { OdabirPojavnogZaDostavuComponent } from './components/actions/odabir-pojavnog-za-dostavu/odabir-pojavnog-za-dostavu.component';
import { OpenPovezanoMjestoActionComponent } from './components/actions/open-povezano-mjesto-action/open-povezano-mjesto-action.component';
import { BasicFileUploadComponent } from './components/basic-file-upload/basic-file-upload.component';
import { OdabirJediniceZaDostavuComponent } from './components/dostave/odabir-jedinice-za-dostavu.component';
import { EarhivPopupComponent } from './components/earhiv-popup/earhiv-popup.component';
import { FastDataEntryContainerComponent } from './components/fast-data-entry-container/fast-data-entry-container.component';
import { FastDataEntryViewItemProviderDirective } from './components/fast-data-entry/service/fast-data-entry-view-item-provider.directive';
import { BreadcrumbsHierarchyComponent } from './components/hierarhija-pozicija/breadcrumbs.component';
import { InplaceTableRowComponent } from './components/inplace-table-row/inplace-table-row.component';
import { JedinicaGradivaListGridViewComponent } from './components/jedinica-gradiva-list-grid-view/jedinica-gradiva-list-grid-view.component';
import { OdabirMjestaComponent } from './components/odabir-mjesta/odabir-mjesta.component';
import { OdabirPovezanogMjestaProviderDirective } from './components/odabir-mjesta/odabir-povezanog-mjesta-provider.directive';
import { OdabirPovezanogMjestaComponent } from './components/odabir-mjesta/odabir-povezanog-mjesta/odabir-povezanog-mjesta.component';
import { OdabirPredmetaComponent } from './components/spajanje_predmeta/odabir-predmeta.component';
import { PoveziPredmetComponent } from './components/spajanje_predmeta/povezi-predmet.component';
import { RazdvojiPredmetComponent } from './components/spajanje_predmeta/razdvoji-predmet.component';
import { CustomItemViewProviderDirective } from './directives/row-item-view-provider.directive';
import { HierarchyModule } from './components/hierarhija-pozicija/hierarchy/hierarchy';
import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental';
import { revalidateHierarchyInterceptor } from './components/hierarhija-pozicija/queries';
import { SelectOsobaComponent } from './components/odabir-osobe/odabir-osobe.component';
import { OdabirPovezaneOsobeComponent } from './components/odabir-osobe/odabir-povezane-osobe.component';
import { OdabirPovezaneOsobeProviderDirective } from './components/odabir-osobe/odabir-povezane-osobe.provider';
import { PromijeniLokacijuFizickiObjektiActionComponent } from './components/actions/promijeni-lokaciju-fizicki-objekti/promijeni-lokaciju-fizicki-objekti';
import { OdabirPovezanogPrimateljaComponent } from './components/odabir-primatelja/odabir-povezanog-primatelja.component';
import { OdabirPovezanogPosiljateljaComponent } from './components/odabir-posiljatelja/odabir-povezanog-posiljatelja.component';
import { ReadJedinicaGradivaDokComponent } from './components/actions/dokumentacija/read-jedinica-gradiva-dok';
import { OibServiceSubjektActionComponent } from './components/actions/dokumentacija/oib-service-subjekt-action/oib-service-subjekt-action.component';
import { VratiPredmetUObraduActionComponent } from './components/actions/vrati-predmet-u-obradu-action/vrati-predmet-u-obradu-action.component';
import { KlasifikacijskiSifrarnikComponent } from './components/klasifikacijski-sifrarnik/klasifikacijski-sifrarnik.component';
import { KopirajDokumentActionComponent } from './components/actions/kopiraj-dokument-action/kopiraj-dokument-action.component';
import { ObrisiDokumentacijaJedinicaListItemActionComponent } from './components/actions/dokumentacija/obrisi-dokumentacija-list-item-action';
import { HierarchyDropdownComponent } from './components/hierarchy-dropdown/hierarchy-dropdown.component';
import { DeleteMultipleDocumentsComponent } from './components/actions/delete-multiple-documents/delete-multiple-documents.component';
import { UpravnaStvarComponent } from './components/upravna_stvar/upravna_stvar.component';
import { UpravnaStvarListComponent } from './components/upravna_stvar/upravna_stvar_list/upravna_stvar_list.component';
import { OpenVezanoGradivoOsobeActionComponent } from './components/actions/open-vezano-gradivo-osobe-action/open-vezano-gradivo-osobe-action.component';
import { UkloniRokCuvanjaActionComponent } from './components/actions/ukloni-rok-cuvanja-na-jedinicama/ukloni-rok-cuvanja-na-jedinicama.component';
import { authInterceptor } from './interceptors/auth-interceptor';
import { InheritedMetadataInputComponent } from './components/inputs/inherited-metadata-input/inherited-metadata-input.component';
import { PojavniInheritedMetadataInputComponent } from './components/inputs/pojavni-inherited-metadata-input/pojavni-inherited-metadata-input.component';
import { NoviDirektorijPojavnogActionComponent } from './components/actions/new-direktorij-action/new-direktorij-action.component';
import { PredajaDeleteActionComponent } from './components/actions/predaja-delete-action/predaja-delete-action.component';
import { EditArhivPosjednikComponent } from './components/actions/edit-arhiv-posjednik/edit-arhiv-posjednik.component';
import { EditPojavniOblikComponent } from './components/actions/edit-pojavni-oblik/edit-pojavni-oblik.component';
import { OdabirJedinicaProviderDirective } from './shared/components/odabir-jedinica-gradiva/odabir-jedinica-gradiva.provider';
import { OdabirJedinicaFilterViewProviderDirective } from './shared/components/odabir-jedinica-gradiva/odabir-jedinica-filter-view-provider.component';
import { SelectJediniceGradivaComponent } from './shared/components/odabir-jedinica-gradiva/select-jedinice-gradiva.component';
import { ItemsListActionComponent } from './components/actions/items-list-action/items-list-action.component';
import { OdabirPojavnogZaIzlucivanjeComponent } from './components/actions/odabir-pojavnog-za-izlucivanje/odabir-pojavnog-za-izlucivanje.component';
import { SelectPojavniIzlucivanjeComponent } from './components/actions/odabir-pojavnog-za-izlucivanje/odabir-pojavnog-izlucivanje.component';
import { OpenKorisnikCitaoniceComponent } from './components/actions/open-korisnik-citaonice/open-korisnik-citaonice.component';
import { TableModule } from 'primeng/table';
import { OdabirJediniceRazmjestenoUComponent } from './components/predaje/odabir-jedinice-razmjesteno-u.component';
import { DeduplicateMjestoActionComponent } from './components/actions/deduplicate-mjesto-action/deduplicate-mjesto-action.component';
import { DeduplikacijaMjestoDropdownComponent } from './components/deduplikacija-mjesto-dropdown/deduplikacija-mjesto-dropdown.component';
import { SaveCloseDeduplicatedMjestoItemActionComponent } from './components/actions/save-close-deduplicated-mjesto-action/save-close-deduplicated-mjesto-action.component';
import { MjestoPrikazComponent } from './components/mjesto-prikaz/mjesto-prikaz.component';
import { DenyMjestoDeduplicationComponent } from './components/actions/deny-mjesto-deduplication/deny-mjesto-deduplication.component';
import { ColumnDisplayComponent } from './components/ntap-list/column-display/column-display.component';
import { OdabirStrankeComponent } from './components/odabir-stranke/odabir-stranke.component';
import { OpenKonverzijaPojavniOblikActionComponent } from './components/actions/open-konverzija-pojavni-oblik/open-konverzija-pojavni-oblik-action.component';
import { OpenGeneratorUrbrojActionComponent } from './components/actions/open-generator-urbroj-action/open-generator-urbroj-action.component';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { UserProfileComponent } from './components/user-profile/user-profile';
import { PreuzmiDatotekeDokumenteComponent } from './components/preuzmi-datoteke-dokumente/preuzmi-datoteke-dokumente.component';
import { EarhivMessageService } from './services/earhiv-message.service';

registerLocaleData(localeHr, 'hr');

// TODO uzeti default config ?
const ntapConfig: CoreConfig = {
  growlBaseZIndex: 2147483647
} as any as CoreConfig;

const overridePrimeConfig = {
  ...NTAPPrimengModule.DEFAULT_CONFIG,
  fileInputConfig: {
    ...NTAPPrimengModule.DEFAULT_CONFIG.fileInputConfig,
    maxFileSize: 1000000000
  }
};
@NgModule({
  declarations: [
    AppComponent,
    MainListViewComponent,
    DeleteListUsersActionComponent,
    ActivateDeactivateListItemsActionComponent,
    EditableItemListComponent,
    OdabirPovezaneOsobeProviderDirective,
    EditableTableComponent,
    ChooseFromPersonNormativeDatabaseComponent,
    SaveCloseArhivPosjednikActionComponent,
    PublishCloseArhivPosjednikActionComponent,
    ArhivskaOznakaComponent,
    JedinicaGradivaComponent,
    ArhivPosjednikComponent,
    PovijestPromjenaComponent,
    NtapListComponent,
    ListToolBarComponent,
    HierarhijaComponent,
    DokumentacijaHierarhijaComponent,
    EditableTableRowComponent,
    InplaceTableRowComponent,
    LockUnlockListItemsActionComponent,
    ActionServerCloseActionComponent,
    ActionSendFormDataActionComponent,
    PojavniOblikComponent,
    FizickiObjektComponent,
    FizickiObjektListaComponent,
    KratkaPovijestPromjenaComponent,
    NewJedinicaGradivaActionComponent,
    TableListGridComponent,
    PretragaKatalogaComponent,
    CommonListGridViewComponent,
    JedinicaGradivaListGridViewComponent,
    TableListGridProviderDirective,
    RegistarArhivaComponent,
    GeonamesIntegrationInput,
    NoviObjektPojavnogActionComponent,
    NoviDirektorijPojavnogActionComponent,
    SaveCloseKreiranjeFizickihObjekataActionComponent,
    SaveCloseRefreshItemActionComponent,
    NewChildEntityActionComponent,
    PublishUnpublishJediniceActionComponent,
    PosaljiNaVerifikacijuActionComponent,
    RadniZadatakComponent,
    PriloziRadnogZadatkaComponent,
    IntegrationSearchComponent,
    ViafIntegrationInput,
    HierarhijaKlasifikacijaComponent,
    DatotekeListaComponent,
    ObjektiPojavnogOblikaComponent,
    TreeViewProviderDirective,
    HierarchyListComponent,
    MainHierarchylistviewComponent,
    HierarchyListViewProviderDirective,
    HierarhijaPozicijaComponent,
    AgentComponent,
    SeupSubjektComponent,
    AgentPrikazComponent,
    AppIzvjestajItemViewComponent,
    AppGenerirajIzvjestajActionComponent,
    AppIzvjestajCommonItemViewComponent,
    JediniceInventureListComponent,
    JedinicaHierarchyPickerComponent,
    PotvrdiPremjestanjeActionComponent,
    PremjestiActionComponent,
    PremjestiDialogComponent,
    DeleteActionComponent,
    IzlucivanjeActionComponent,
    PredajaComponent,
    JedinicaGradivaIzlucivanjeListaComponent,
    JedinicaGradivaPredajaListaComponent,
    SaveCloseIzlucivanjeActionComponent,
    PotvrdiVrstaZahtjevaActionComponent,
    PredajaActionComponent,
    DeleteItemsListComponent,
    ConfirmPovratFizickogObjektaActionComponent,
    ConfirmDostavuFizickihObjekataActionComponent,
    ConfirmReturnFizickiObjektiToSpremisteActionComponent,
    HierarhijaPosebniPopisComponent,
    VrijemeRangeKompleksniComponent,
    VrijemeKompleksniComponent,
    PretragaKatalogaProviderDirective,
    DostavaActionComponent,
    FastDataEntryComponent,
    FastDataEntryInputBarComponent,
    MojaInstitucijaComponent,
    UserProfileComponent,
    ObjavaOdobjavaJediniceComponent,
    NewFolderActionComponent,
    EditJedinicaGradivaDokComponent,
    ReadJedinicaGradivaDokComponent,
    // PrimeNGTableSearchComponent,
    FastDataEntryEditTemplateBarComponent,
    LazyDropdownInputControlComponent,
    DeduplicateActionComponent,
    DeduplikacijaAgentDropdownComponent,
    RokCuvanjaComponent,
    SaveCloseDeduplicatedAgentItemActionComponent,
    AddRelatedItemEntityComponent,
    NoopViewProviderDirective,
    MjestoComponent,
    ExportAgentDataActionComponent,
    ExportAgentDrugiZapisiComponent,
    EntryTemplateDropdownComponent,
    SpremisteObjektiComponent,
    SpremisteProstorijeComponent,
    ZaglavljeComponent,
    SpremisteObjektiViewComponent,
    EditActionbarComponent,
    InMemoryListViewProviderDirective,
    ExportJediniceGradivaExcelActionComponent,
    MojeGradivoComponent,
    ObjektiFizickogObjektaListComponent,
    LocationFromStorageComponent,
    NazivPojavnogOblikaComponent,
    OpenArhivPosjednikIzJediniceGradivaComponent,
    OdabirJediniceZaRestauracijuComponent,
    PoveziPredmetComponent,
    RazdvojiPredmetComponent,
    SelectJedinicaGradivaComponent,
    OdabirPredmetaComponent,
    OdabirJediniceZaRestauracijuProviderDirective,
    DodajPovezaneJediniceGradivaIzPostojecihComponent,
    NapomenaIzlucivanjeListaComponent,
    NapomenaPredajaListaComponent,
    PrintLabelPdfActionComponent,
    PrintLabelSpremistePdfActionComponent,
    PrintLabelDostavaPdfActionComponent,
    PrintPotvrdaPrimitkaActionComponent,
    IspisOmotaSpisaActionComponent,
    ListAndDetailActionComponent,
    EditJedinicaItemActionComponent,
    EditInventuraItemActionComponent,
    Spremiste2DComponent,
    Spremiste2DGrafikaActionComponent,
    OdabirJediniceZaIstaknutiSadrzajComponent,
    RestauracijaComponent,
    PredmetiRestauracijeComponent,
    OdabirPojavnogZaRestauracijuComponent,
    SelectPojavniComponent,
    SelectFizickiComponent,
    OdabirFizickogZaRestauracijuComponent,
    NapomenaIzlucivanjeListaComponent,
    HomepageSegmentComponent,
    HomePageComponent,
    SpremisteSpremisneJediniceComponent,
    SpremisteLokacijeComponent,
    DodajPovezaneJediniceGradivaIzPostojecihComponent,
    AddObjectToParentActionComponent,
    RazmjestiNizeComponent,
    RazmjestiNizeActionComponent,
    GenerirajJedinicaGradivaIzlucivanjeRelActionComponent,
    DodajJediniceGradivaUZahtjevComponent,
    MicanjeJediniceIzIstaknutogSadrzajaComponent,
    EditableTableAdditionalActionsComponent,
    PremjestiDokJedinicuActionComponent,
    PremjestiDokumentActionComponent,
    KopirajDokumentActionComponent,
    NijeInventuraItemsListComponent,
    UkloniFizickiComponent,
    SudskiRegistarPanelComponent,
    OibServiceDialogComponent,
    OibServicePanelComponent,
    OibServiceSubjektActionComponent,
    ObustaviRokCuvanjaActionComponent,
    PregledGradivaPoRokuCuvanjaComponent,
    DenyAgentVerificationComponent,
    OdabirFizickogUSpremisteComponent,
    OdabirPretrageComponent,
    DatotekaComponent,
    ArhivskaKlasifikacijaComponent,
    OdabirKorisnikaCitaoniceComponent,
    SelectKorisnikCitaoniceComponent,
    SaveNoviKorisnikCitaoniceComponent,
    OdabirJediniceZaPredajuComponent,
    OdabirPojavnogZaPredajuComponent,
    OdabirFizickogZaPredajuComponent,
    SelectPojavniPredajaComponent,
    SelectFizickiPredajaComponent,
    PosaljiUEkulturuActionComponent,
    DeleteObrazacUpisaActionComponent,
    EditObrazacUpisaActionComponent,
    SpremiDatotekeActionComponent,
    ActionSaveMjestoComponent,
    VerificirajActionComponent,
    PromjenaPoretkaActionComponent,
    BasicItemsListActionComponent,
    ZakljucenjePredmetaActionComponent,
    DownloadActionComponent,
    OpenFileInNewTabActionComponent,
    PreuzmiDatotekeDokumenteComponent,
    RazmjestiIzViseComponent,
    SaveOtpremaStatusAction,
    RazmjestiDatotekeIzViseComponent,
    ConfirmationJediniceGradivaComponent,
    SaveOpenNewPodrazinaComponent,
    OpenPovezanaJedinicaGradivaActionComponent,
    FastDataEntryObrasciListProviderDirective,
    VerifyAgentsComponent,
    PublishUnpublishAgentsComponent,
    BrziObrazacStateServiceProviderDirective,
    SelectAgentComponent,
    SelectOsobaComponent,
    OdabirPovezanogAgentaComponent,
    OdabirPovezaneOsobeComponent,
    OdabirPovezanogPrimateljaComponent,
    OdabirPovezanogPosiljateljaComponent,
    OdabirPovezanogAgentaProviderDirective,
    OpenPovezaniAgentActionComponent,
    OdabirStrankeComponent,
    LocateInDokumentacijaTreeActionComponent,
    FileUploadProgressToastComponent,
    OdabirJediniceZaDostavuComponent,
    OdabirPojavnogZaDostavuComponent,
    OdabirFizickogZaDostavuComponent,
    SelectPojavniDostavaComponent,
    SelectFizickiDostavaComponent,
    EarhivPrimeNGInputMultiSelectComponent,
    FastDataEntryContainerComponent,
    FastDataEntryViewItemProviderDirective,
    EarhivPopupComponent,
    OdabirPovezanogMjestaComponent,
    OdabirMjestaComponent,
    OdabirPovezanogMjestaProviderDirective,
    OpenPovezanoMjestoActionComponent,
    EditAgentItemActionComponent,
    ExportJediniceGradivaActionComponent,
    CustomItemViewProviderDirective,
    BreadcrumbsHierarchyComponent,
    PromijeniLokacijuFizickiObjektiActionComponent,
    BasicFileUploadComponent,
    ConfirmEkulturaDialog,
    IzradiKlasifikacijskuOznakuComponent,
    VratiPredmetUObraduActionComponent,
    KlasifikacijskiSifrarnikComponent,
    UpravnaStvarComponent,
    UpravnaStvarListComponent,
    ObrisiDokumentacijaJedinicaListItemActionComponent,
    HierarchyDropdownComponent,
    DeleteMultipleDocumentsComponent,
    ConfirmIzdavanjeGradivaActionComponent,
    AddParentToKlasaActionComponent,
    KlasaListComponent,
    KlasaHierarchyListViewProviderDirective,
    SaveCloseRefreshDokActionComponent,
    SaveRefreshDokActionComponent,
    OpenVezanoGradivoOsobeActionComponent,
    UkloniRokCuvanjaActionComponent,
    InheritedMetadataInputComponent,
    PojavniInheritedMetadataInputComponent,
    PredajaDeleteActionComponent,
    EditArhivPosjednikComponent,
    EditPojavniOblikComponent,
    SetStatusOtpremeActionComponent,
    SelectJediniceGradivaComponent,
    OdabirJedinicaProviderDirective,
    OdabirJedinicaFilterViewProviderDirective,
    NewZahtjevActionComponent,
    ItemsListActionComponent,
    SelectPojavniIzlucivanjeComponent,
    OdabirPojavnogZaIzlucivanjeComponent,
    OpenKorisnikCitaoniceComponent,
    OdabirJediniceRazmjestenoUComponent,
    DeduplicateMjestoActionComponent,
    DeduplikacijaMjestoDropdownComponent,
    SaveCloseDeduplicatedMjestoItemActionComponent,
    MjestoPrikazComponent,
    DenyMjestoDeduplicationComponent,
    PromjenaZaduzenjaZaAgentaOpenItemViewActionComponent,
    EditDokumentacijaDatotekaActionComponent,
    ActivateDeactivateKlasaListItemsActionComponent,
    ObrisiKlasaItemActionComponent,
    CommonItemViewComponent,
    NewPredmetActionComponent,
    NewDokumentActionComponent,
    LockUnlockDokumentacijaListItemsActionComponent,
    NewKlasaActionComponent,
    OpenKonverzijaPojavniOblikActionComponent,
    KonvertirajPojavniOblikActionComponent,
    OpenGeneratorUrbrojActionComponent
  ],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputNumberModule,
    AppRoutingModule,
    AppLayoutModule,
    CardModule,
    ToolbarModule,
    NTAPCoreModule.forRoot(ntapConfig),
    NTAPCommonModule.forRoot(ntapConfig),
    ConfigModule.forRoot(ntapConfig),
    NTAPPrimengObrasciModule.forRoot(),
    TreeModule,
    TreeSelectModule,
    DragDropModule,
    DataViewModule,
    PanelModule,
    SharedModule,
    AutoCompleteModule,
    TreeTableModule,
    InplaceModule,
    ConfirmDialogModule,
    SelectButtonModule,
    ToggleButtonModule,
    OverlayPanelModule,
    RadioButtonModule,
    DividerModule,
    BreadcrumbModule,
    BadgeModule,
    SplitterModule,
    ListboxModule,
    PaginatorModule,
    ScrollerModule,
    DialogModule,
    FieldsetModule,
    ButtonModule,
    NotificationModule,
    HierarchyModule,
    RouterLink,
    AngularQueryDevtools,
    TableModule,
    SkeletonModule,
    ColumnDisplayComponent,
    MessageModule
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'hr' },
    { provide: NTAPMenuService, useClass: ArhivMenuService },
    { provide: MessageService, useClass: EarhivMessageService },
    ConfirmationService,
    TreeViewProviderDirective,
    ItemViewProviderDirective,
    IzvjestajDownloadService,
    ContentDownloadService,
    RefreshableAttributeStateService,
    KlasaHierarchyListViewProviderDirective,
    {
      provide: AttributeStateService,
      useExisting: RefreshableAttributeStateService
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([revalidateHierarchyInterceptor, authInterceptor])
    ),
    provideAngularQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            retryDelay: (attemptIndex) =>
              Math.min(1000 * 2 ** attemptIndex, 30000),
            retry: 5
          }
        }
      })
    ),
    { provide: PrimengConfigService, useValue: overridePrimeConfig }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  CUSTOM_REGISTRY = new Map<string, Type<any>>([
    [
      EarhivUiComponents.DELETE_LIST_USERS_ACTION,
      DeleteListUsersActionComponent
    ],
    [
      EarhivUiComponents.ACTIVATE_DEACTIVATE_ITEMS_ACTION,
      ActivateDeactivateListItemsActionComponent
    ],
    [EarhivUiComponents.EDITABLE_ITEMLIST_INPUT, EditableItemListComponent],
    [
      EarhivUiComponents.CHOOSE_FROM_PERSON_NORMATIVE_DB_ACTION,
      ChooseFromPersonNormativeDatabaseComponent
    ],
    [
      EarhivUiComponents.SAVE_CLOSE_ARHIV_POSJEDNIK_ACTION,
      SaveCloseArhivPosjednikActionComponent
    ],
    [
      EarhivUiComponents.PUBLISH_CLOSE_ARHIV_POSJEDNIK_ACTION,
      PublishCloseArhivPosjednikActionComponent
    ],
    [
      EarhivUiComponents.LOCK_UNLOCK_ITEMS_ACTION,
      LockUnlockListItemsActionComponent
    ],
    [EarhivUiComponents.SERVER_CLOSE, ActionServerCloseActionComponent],
    [EarhivUiComponents.SEND_FORM_DATA, ActionSendFormDataActionComponent],
    [EarhivUiComponents.ARHIVSKA_OZNAKA_INPUT, ArhivskaOznakaComponent],
    [EarhivUiComponents.AGENT_ITEM_VIEW, AgentComponent],
    [EarhivUiComponents.SEUP_SUBJEKT_ITEM_VIEW, SeupSubjektComponent],
    [EarhivUiComponents.PREDAJA_ITEM_VIEW, PredajaComponent],
    [EarhivUiComponents.JEDINICA_GRADIVA_ITEM_VIEW, JedinicaGradivaComponent],
    [EarhivUiComponents.SPREMISTE_OBJEKTI_VIEW, SpremisteObjektiViewComponent],
    [
      EarhivUiComponents.SPREMISTE_PROSTORIJE_VIEW,
      SpremisteProstorijeComponent
    ],
    [
      EarhivUiComponents.SPREMISTE_SPREMISNA_JEDINICA_VIEW,
      SpremisteSpremisneJediniceComponent
    ],
    [EarhivUiComponents.SPREMISTE_LOKACIJA_VIEW, SpremisteLokacijeComponent],
    [EarhivUiComponents.ARHIV_POSJEDNIK_ITEM_VIEW, ArhivPosjednikComponent],
    [EarhivUiComponents.POVIJEST_PROMJENA_ITEM_VIEW, PovijestPromjenaComponent],
    [
      EarhivUiComponents.NAPOMENE_IZLUCIVANJE_LIST_VIEW,
      NapomenaIzlucivanjeListaComponent
    ],
    [
      EarhivUiComponents.NAPOMENE_PREDAJA_LIST_VIEW,
      NapomenaPredajaListaComponent
    ],
    [EarhivUiComponents.POJAVNI_OBLIK_ITEM_VIEW, PojavniOblikComponent],
    [EarhivUiComponents.FIZICKI_OBJEKT_ITEM_VIEW, FizickiObjektComponent],
    [EarhivUiComponents.TABLE_LIST_GRID_VIEW, TableListGridComponent],
    [
      EarhivUiComponents.OBRISI_DOKUMENTACIJA_JEDINICA_LIST_ITEM,
      ObrisiDokumentacijaJedinicaListItemActionComponent
    ],
    [
      EarhivUiComponents.COMMON_TABLE_LIST_GRID_VIEW,
      CommonListGridViewComponent
    ],
    [EarhivUiComponents.TASK_ITEM_VIEW, RadniZadatakComponent],

    [EarhivUiComponents.GEONAMES_INTEGRATION_INPUT, GeonamesIntegrationInput],
    [EarhivUiComponents.VIAF_INTEGRATION_INPUT, ViafIntegrationInput],
    ['confirmVrstaZahtjeva', PotvrdiVrstaZahtjevaActionComponent],
    [
      EarhivUiComponents.NEW_JEDINICA_GRADIVA_ACTION,
      NewJedinicaGradivaActionComponent
    ],
    [EarhivUiComponents.NEW_IZLUCIVANJE_ACTION, IzlucivanjeActionComponent],
    [
      EarhivUiComponents.NEW_OBJEKTI_POJAVNOG_OBLIKA_ACTION,
      NoviObjektPojavnogActionComponent
    ],
    [EarhivUiComponents.NEW_CHILD_ENTITY_ACTION, NewChildEntityActionComponent],
    [
      EarhivUiComponents.SAVE_CLOSE_KREIRANJE_FIZICKI_OBJEKTI_ACTION,
      SaveCloseKreiranjeFizickihObjekataActionComponent
    ],
    [
      EarhivUiComponents.SAVE_CLOSE_REFRESH_ACTION,
      SaveCloseRefreshItemActionComponent
    ],
    [
      EarhivUiComponents.GENERIRAJ_JEDINICA_GRADIVA_IZLUCIVANJE_REL_ACTION,
      GenerirajJedinicaGradivaIzlucivanjeRelActionComponent
    ],
    [
      EarhivUiComponents.OBUSTAVI_ROK_CUVANJA,
      ObustaviRokCuvanjaActionComponent
    ],
    [
      EarhivUiComponents.IZRADI_KLASIFIKACIJSKU_OZNAKU,
      IzradiKlasifikacijskuOznakuComponent
    ],
    [
      EarhivUiComponents.PUBLISH_UNPUBLISH_JEDINICE_ACTION,
      PublishUnpublishJediniceActionComponent
    ],
    [
      EarhivUiComponents.SAVE_STATUS_OTPREME_FOR_MORE_OTPREMAS,
      SaveOtpremaStatusAction
    ],
    [
      EarhivUiComponents.SEND_TO_VERIFICATION_ACTION,
      PosaljiNaVerifikacijuActionComponent
    ],
    [UIComponents.LISTVIEW_TABLE, NtapListComponent],
    [UIComponents.LISTTOOLBAR, ListToolBarComponent],
    [UIComponents.INPUT_MULTI_SELECT, EarhivPrimeNGInputMultiSelectComponent],
    [UIComponents.ITEMVIEW, CommonItemViewComponent],
    [UIComponents.ITEMVIEW_POPUP, EarhivPopupComponent],
    [EarhivUiComponents.TREELISTVIEW, HierarchyListComponent],
    [EarhivUiComponents.IZVJESTAJ_ITEM_VIEW, AppIzvjestajItemViewComponent],
    ['izvjestaj_item_view', AppIzvjestajCommonItemViewComponent],
    [
      EarhivUiComponents.JEDINICE_INVENTURE_ITEM_VIEW,
      JediniceInventureListComponent
    ],
    [
      EarhivUiComponents.POTVRDI_PREMJESTANJE_JEDINICE,
      PotvrdiPremjestanjeActionComponent
    ],
    [EarhivUiComponents.PREMJESTI_JEDINICE, PremjestiActionComponent],
    [
      EarhivUiComponents.DELETE_LIST_ITEMS_ARHIV_ITEM_ACTION,
      DeleteActionComponent
    ],
    [
      EarhivUiComponents.SPREMI_IZLUCIVANJE_ACTION,
      SaveCloseIzlucivanjeActionComponent
    ],
    [EarhivUiComponents.NEW_PREDAJA_ACTION, PredajaActionComponent],

    [EarhivUiComponents.DELETE_LIST_ITEMS_ACTION, DeleteItemsListComponent],
    [EarhivUiComponents.NIJE_INVENTURA, NijeInventuraItemsListComponent],
    [EarhivUiComponents.UKLONI_FIZICKI, UkloniFizickiComponent],
    [
      EarhivUiComponents.POTVRDI_POVRAT_FIZICKOG_OBJEKTA_ACTION,
      ConfirmPovratFizickogObjektaActionComponent
    ],
    [
      EarhivUiComponents.CONFIRM_DOSTAVU_FIZICKIH_OBJEKATA,
      ConfirmDostavuFizickihObjekataActionComponent
    ],
    [
      EarhivUiComponents.RETURN_FIZICKI_OBJEKT_TO_SPREMISTE,
      ConfirmReturnFizickiObjektiToSpremisteActionComponent
    ],
    [EarhivUiComponents.DOSTAVA_JEDINICA_ACTION, DostavaActionComponent],
    [
      EarhivUiComponents.VRIJEME_RANGE_KOMPLEKSNI_INPUT,
      VrijemeRangeKompleksniComponent
    ],
    [EarhivUiComponents.VRIJEME_KOMPLEKSNI_INPUT, VrijemeKompleksniComponent],
    [
      EarhivUiComponents.OBJAVA_ODOBJAVA_JEDINICA_ACTION,
      ObjavaOdobjavaJediniceComponent
    ],
    [EarhivUiComponents.LAZY_DROPDOWN_INPUT, LazyDropdownInputControlComponent],
    [EarhivUiComponents.LOCATION_FROM_STORAGE, LocationFromStorageComponent],
    [
      EarhivUiComponents.DEDUPLICATION_AGENT_DROPDOWN,
      DeduplikacijaAgentDropdownComponent
    ],
    [EarhivUiComponents.ROK_CUVANJA_TREE_INPUT, RokCuvanjaComponent],
    [
      EarhivUiComponents.DEDUPLICATE_LIST_ITEMS_ACTION,
      DeduplicateActionComponent
    ],
    [
      EarhivUiComponents.SAVE_CLOSE_DEDUPLICATE_AGENT_ITEMS_ACTION,
      SaveCloseDeduplicatedAgentItemActionComponent
    ],
    [
      EarhivUiComponents.ADD_POPIS_ROKOVA_CUVANJA_FOR_SPECIFIC_PARENT_ID,
      AddObjectToParentActionComponent
    ],
    [EarhivUiComponents.OPEN_NESTED_VIEW_ACTION, AddRelatedItemEntityComponent],
    [EarhivUiComponents.MJESTO_ITEM_VIEW, MjestoComponent],
    [
      EarhivUiComponents.EXPORT_AGENT_DATA_ACTION,
      ExportAgentDataActionComponent
    ],
    [
      EarhivUiComponents.EXPORT_AGENT_DRUGI_ZAPISI_ACTION,
      ExportAgentDrugiZapisiComponent
    ],
    [EarhivUiComponents.EDIT_ACTIONBAR, EditActionbarComponent],
    [EarhivUiComponents.NEW_FOLDER_ACTION, NewFolderActionComponent],
    [
      EarhivUiComponents.EDIT_JEDINICA_GRADIVA_DOKUMENTACIJA,
      EditJedinicaGradivaDokComponent
    ],
    [
      EarhivUiComponents.READ_JEDINICA_GRADIVA_DOKUMENTACIJA,
      ReadJedinicaGradivaDokComponent
    ],
    [
      EarhivUiComponents.EXPORT_JEDINICE_GRADIVA_XLSX_ACTION,
      ExportJediniceGradivaExcelActionComponent
    ],
    [
      EarhivUiComponents.POJAVNI_OBLIK_NAZIV_INPUT,
      NazivPojavnogOblikaComponent
    ],
    [
      EarhivUiComponents.OPEN_ARHIV_POSJEDNIK_FROM_JEDINICA_GRADIVA_ACTION,
      OpenArhivPosjednikIzJediniceGradivaComponent
    ],
    ['odabirJediniceZaRestauraciju', OdabirJediniceZaRestauracijuComponent],
    [EarhivUiComponents.POVEZI_DOKUMENTACIJA_PREDMET, PoveziPredmetComponent],
    [
      EarhivUiComponents.RAZDVOJI_DOKUMENTACIJA_PREDMET,
      RazdvojiPredmetComponent
    ],
    [
      EarhivUiComponents.ODABIR_POVEZANE_JEDINICE_GRADIVA_IZ_POSTOJECIH,
      DodajPovezaneJediniceGradivaIzPostojecihComponent
    ],
    [EarhivUiComponents.PRINT_LABEL_PDF_ACTION, PrintLabelPdfActionComponent],
    [
      EarhivUiComponents.PRINT_LABEL_SPREMSITE_PDF_ACTION,
      PrintLabelSpremistePdfActionComponent
    ],
    [
      EarhivUiComponents.PRINT_LABEL_DOSTAVA_PDF_ACTION,
      PrintLabelDostavaPdfActionComponent
    ],
    [
      EarhivUiComponents.PRINT_POTVRDA_PRIMITKA_ACTION,
      PrintPotvrdaPrimitkaActionComponent
    ],
    [
      EarhivUiComponents.PRINT_OMOTA_SPISA_ACTION,
      IspisOmotaSpisaActionComponent
    ],
    [EarhivUiComponents.LIST_AND_DIALOG_ACTION, ListAndDetailActionComponent],
    [EarhivUiComponents.EDIT_JEDINICA_ITEM, EditJedinicaItemActionComponent],
    [EarhivUiComponents.EDIT_INVENTURA_ITEM, EditInventuraItemActionComponent],
    [EarhivUiComponents.RESTAURACIJA_ITEM_VIEW, RestauracijaComponent],
    [
      EarhivUiComponents.SPREMISTE_2D_PREGLED_ACTION,
      Spremiste2DGrafikaActionComponent
    ],
    [
      EarhivUiComponents.ODABIR_JEDINICE_GRADIVA_ZA_ISTAKNUTI_SADRZAJ,
      OdabirJediniceZaIstaknutiSadrzajComponent
    ],
    [
      EarhivUiComponents.MICANJE_JEDINICE_IZ_ISTAKNUTOG_SADRZAJA,
      MicanjeJediniceIzIstaknutogSadrzajaComponent
    ],
    [EarhivUiComponents.RESTAURACIJA_ITEM_VIEW, RestauracijaComponent],
    [
      EarhivUiComponents.PREDMET_RESTAURACIJE_VIEW,
      PredmetiRestauracijeComponent
    ],
    [
      EarhivUiComponents.ODABIR_POJAVOG_OBLIKA_ZA_RESTAURACIJU,
      OdabirPojavnogZaRestauracijuComponent
    ],
    [
      EarhivUiComponents.ODABIR_FIZICKOG_OBJEKTA_ZA_RESTAURACIJU,
      OdabirFizickogZaRestauracijuComponent
    ],
    [
      EarhivUiComponents.ODABIR_FIZICKOG_OBJEKTA_U_SPREMISTE,
      OdabirFizickogUSpremisteComponent
    ],
    [EarhivUiComponents.RAZMJESTI_NIZE_LIST, RazmjestiNizeComponent],
    [
      EarhivUiComponents.RAZMJESTI_NIZE_LIST_ITEMS_ACTION,
      RazmjestiNizeActionComponent
    ],
    [
      EarhivUiComponents.DODAJ_JEDINICE_GRADIVA_U_ZAHTJEV,
      DodajJediniceGradivaUZahtjevComponent
    ],
    [
      EarhivUiComponents.PREMJESTI_DOK_JEDINICU_ACTION,
      PremjestiDokJedinicuActionComponent
    ],
    [
      EarhivUiComponents.PREMJESTI_DOKUMENT_ACTION,
      PremjestiDokumentActionComponent
    ],
    [
      EarhivUiComponents.DENY_AGENT_VERIFICATION,
      DenyAgentVerificationComponent
    ],
    [EarhivUiComponents.DATOTEKA_ITEM_VIEW, DatotekaComponent],
    [EarhivUiComponents.ARHIVSKA_KLASIFIKACIJA, ArhivskaKlasifikacijaComponent],
    [
      EarhivUiComponents.ODABIR_KORISNIKA_CITAONICE,
      OdabirKorisnikaCitaoniceComponent
    ],
    [
      EarhivUiComponents.SAVE_CLOSE_KORISNIK_CITAONICE,
      SaveNoviKorisnikCitaoniceComponent
    ],
    [EarhivUiComponents.DATOTEKA_ITEM_VIEW, DatotekaComponent],
    [
      EarhivUiComponents.ODABIR_JEDINICE_PREDAJA,
      OdabirJediniceZaPredajuComponent
    ],
    [
      EarhivUiComponents.ODABIR_POJAVOG_OBLIKA_ZA_PREDAJU,
      OdabirPojavnogZaPredajuComponent
    ],
    [
      EarhivUiComponents.ODABIR_FIZICKOG_OBJEKTA_ZA_PREDAJU,
      OdabirFizickogZaPredajuComponent
    ],
    [
      EarhivUiComponents.ODABIR_JEDINICE_DOSTAVA,
      OdabirJediniceZaDostavuComponent
    ],
    [
      EarhivUiComponents.ODABIR_POJAVOG_OBLIKA_ZA_DOSTAVU,
      OdabirPojavnogZaDostavuComponent
    ],
    [
      EarhivUiComponents.ODABIR_FIZICKOG_OBJEKTA_ZA_DOSTAVU,
      OdabirFizickogZaDostavuComponent
    ],
    [
      EarhivUiComponents.POSALJI_U_EKULTURU_ACTION,
      PosaljiUEkulturuActionComponent
    ],
    [
      EarhivUiComponents.DELETE_OBRAZAC_UPISA_ACTION,
      DeleteObrazacUpisaActionComponent
    ],
    [EarhivUiComponents.SPREMI_DATOTEKE_ACTION, SpremiDatotekeActionComponent],
    [EarhivUiComponents.ACTION_SAVE_MJESTO, ActionSaveMjestoComponent],
    [EarhivUiComponents.VERIFICIRAJ_ACTION, VerificirajActionComponent],
    [
      EarhivUiComponents.PROMJENA_PORETKA_ACTION,
      PromjenaPoretkaActionComponent
    ],
    [EarhivUiComponents.BASIC_ITEMS_LIST_ACTION, BasicItemsListActionComponent],
    [
      EarhivUiComponents.PROMJENA_PORETKA_ACTION,
      PromjenaPoretkaActionComponent
    ],
    [
      EarhivUiComponents.ZAKLJUCENJE_PREDMETA_ACTION,
      ZakljucenjePredmetaActionComponent
    ],
    [
      EarhivUiComponents.VRACANJE_PREDMETA_U_OBRADU_ACTION,
      VratiPredmetUObraduActionComponent
    ],
    [EarhivUiComponents.PREUZMI_DOK_FOLDER, DownloadActionComponent],
    [EarhivUiComponents.OTVORI_DOK_U_TABU, OpenFileInNewTabActionComponent],
    [
      EarhivUiComponents.PREUZMI_DOK_MULTIPLE_FILES,
      PreuzmiDatotekeDokumenteComponent
    ],
    [
      EarhivUiComponents.PROMJENA_PORETKA_ACTION,
      PromjenaPoretkaActionComponent
    ],
    [
      EarhivUiComponents.FIZICKI_OBJEKTI_VISIH_RAZINA_LIST_VIEW,
      RazmjestiIzViseComponent
    ],
    [
      EarhivUiComponents.DATOTEKE_VISIH_RAZINA_LIST_VIEW,
      RazmjestiDatotekeIzViseComponent
    ],
    [
      EarhivUiComponents.CONFIRMATION_JEDINICE_GRADIVA,
      ConfirmationJediniceGradivaComponent
    ],
    [EarhivUiComponents.SAVE_OPEN_NEW_PODRAZINA, SaveOpenNewPodrazinaComponent],
    [
      EarhivUiComponents.OPEN_POVEZANA_JEDINICA_GRADIVA_ACTION,
      OpenPovezanaJedinicaGradivaActionComponent
    ],
    [EarhivUiComponents.VERIFY_AGENTS_ACTION, VerifyAgentsComponent],
    [
      EarhivUiComponents.PUBLISH_UNPUBLISH_AGENTS,
      PublishUnpublishAgentsComponent
    ],
    [
      EarhivUiComponents.ODABIR_POVEZANOG_AGENTA,
      OdabirPovezanogAgentaComponent
    ],
    [EarhivUiComponents.ODABIR_POVEZANE_OSOBE, OdabirPovezaneOsobeComponent],
    [
      EarhivUiComponents.ODABIR_POVEZANOG_PRIMATELJA,
      OdabirPovezanogPrimateljaComponent
    ],
    [
      EarhivUiComponents.ODABIR_POVEZANOG_POSILJATELJA,
      OdabirPovezanogPosiljateljaComponent
    ],
    [EarhivUiComponents.ODABIR_STRANKE, OdabirStrankeComponent],
    [
      EarhivUiComponents.OPEN_POVEZANI_AGENT_ACTION,
      OpenPovezaniAgentActionComponent
    ],
    [
      EarhivUiComponents.LOCATE_IN_DOKUMENTACIJA_TREE,
      LocateInDokumentacijaTreeActionComponent
    ],
    [
      EarhivUiComponents.ODABIR_POVEZANOG_MJESTA,
      OdabirPovezanogMjestaComponent
    ],
    [
      EarhivUiComponents.OPEN_POVEZANO_MJESTO_ACTION,
      OpenPovezanoMjestoActionComponent
    ],
    [EarhivUiComponents.OPEN_EDIT_AGENT_ACTION, EditAgentItemActionComponent],
    [
      EarhivUiComponents.EXPORT_JEDINICE_GRADIVA_ACTION,
      ExportJediniceGradivaActionComponent
    ],
    [
      EarhivUiComponents.JEDINICA_GRADIVA_TABLE_LIST_GRID_VIEW,
      JedinicaGradivaListGridViewComponent
    ],
    [
      EarhivUiComponents.PROMIJENI_LOKACIJU_FIZICKI_OBJEKTI_ACTION,
      PromijeniLokacijuFizickiObjektiActionComponent
    ],
    [EarhivUiComponents.BASIC_FILE_INPUT, BasicFileUploadComponent],
    [
      EarhivUiComponents.ROK_CUVANJA_HIERARCHY_DROPDOWN,
      HierarchyDropdownComponent
    ],
    [
      EarhivUiComponents.KLASIFIKACIJSKI_SIFRARNIK_INPUT,
      KlasifikacijskiSifrarnikComponent
    ],
    [
      EarhivUiComponents.KOPIRAJ_DOKUMENT_ACTION,
      KopirajDokumentActionComponent
    ],
    [
      EarhivUiComponents.DELETE_MULTIPLE_DOCUMENTS_ACTION,
      DeleteMultipleDocumentsComponent
    ],
    [
      EarhivUiComponents.POTVRDI_IZDAVANJE_FIZICKOG_OBJEKTA_ACTION,
      ConfirmIzdavanjeGradivaActionComponent
    ],
    [EarhivUiComponents.ADD_PARENT_TO_KLASA, AddParentToKlasaActionComponent],
    [
      EarhivUiComponents.SAVE_CLOSE_REFRESH_DOKUMENTACIJA_ACTION,
      SaveCloseRefreshDokActionComponent
    ],
    [
      EarhivUiComponents.SAVE_REFRESH_DOKUMENTACIJA_ACTION,
      SaveRefreshDokActionComponent
    ],
    [EarhivUiComponents.UPRAVNA_STVAR_INPUT, UpravnaStvarComponent],
    [
      EarhivUiComponents.OPEN_VEZANO_GRADIVO_OSOBA_ACTION,
      OpenVezanoGradivoOsobeActionComponent
    ],
    [
      EarhivUiComponents.UKLONI_ROK_CUVANJA_ACTION,
      UkloniRokCuvanjaActionComponent
    ],
    [
      EarhivUiComponents.JEDINICA_GRADIVA_INHERITABLE_ATTRIBUTE_INPUT,
      InheritedMetadataInputComponent
    ],
    [
      EarhivUiComponents.POJAVNI_OBLIK_INHERITABLE_ATTRIBUTE_INPUT,
      PojavniInheritedMetadataInputComponent
    ],
    [
      EarhivUiComponents.NEW_DIREKTORIJ_POJAVNOG,
      NoviDirektorijPojavnogActionComponent
    ],
    [EarhivUiComponents.PREDAJA_DELETE_ACTION, PredajaDeleteActionComponent],
    [
      EarhivUiComponents.EDIT_ARHIV_POSJEDNIK_ACTION,
      EditArhivPosjednikComponent
    ],
    [EarhivUiComponents.EDIT_POJAVNI_OBLIK_ACTION, EditPojavniOblikComponent],
    [
      EarhivUiComponents.POSTAVI_STATUS_OTPREME_ACTION,
      SetStatusOtpremeActionComponent
    ],
    [EarhivUiComponents.NEW_ZAHTJEV_ACTION, NewZahtjevActionComponent],
    [EarhivUiComponents.DISABLE_FOR_UNSELECTED, ItemsListActionComponent],
    [
      EarhivUiComponents.ODABIR_POJAVOG_OBLIKA_ZA_IZLUCIVANJE,
      OdabirPojavnogZaIzlucivanjeComponent
    ],
    [
      EarhivUiComponents.OPEN_KORISNIK_CITAONICE,
      OpenKorisnikCitaoniceComponent
    ],
    [
      EarhivUiComponents.ODABIR_JEDINICE_RAZMJESTENO_U,
      OdabirJediniceRazmjestenoUComponent
    ],
    [
      EarhivUiComponents.DEDUPLICATE_MJESTO_LIST_ITEMS_ACTION,
      DeduplicateMjestoActionComponent
    ],
    [
      EarhivUiComponents.DEDUPLICATION_MJESTO_DROPDOWN,
      DeduplikacijaMjestoDropdownComponent
    ],
    [
      EarhivUiComponents.SAVE_CLOSE_DEDUPLICATED_MJESTO_ITEMS_ACTION,
      SaveCloseDeduplicatedMjestoItemActionComponent
    ],
    [
      EarhivUiComponents.DENY_MJESTO_DEDUPLICATION_COMPONENT,
      DenyMjestoDeduplicationComponent
    ],
    [
      EarhivUiComponents.CHANGE_AGENT_ASSIGNED_USER,
      PromjenaZaduzenjaZaAgentaOpenItemViewActionComponent
    ],
    [
      EarhivUiComponents.EDIT_DOKUMENTACIJA_DATOTEKA_ACTION,
      EditDokumentacijaDatotekaActionComponent
    ],
    [
      EarhivUiComponents.ACTIVATE_DEACTIVATE_KLASA_ITEMS_ACTION,
      ActivateDeactivateKlasaListItemsActionComponent
    ],
    [
      EarhivUiComponents.OBRISI_KLASA_LIST_ITEM_ACTION,
      ObrisiKlasaItemActionComponent
    ],
    [EarhivUiComponents.NEW_PREDMET_ACTION, NewPredmetActionComponent],
    [EarhivUiComponents.NEW_DOKUMENT_ACTION, NewDokumentActionComponent],
    [
      EarhivUiComponents.LOCK_UNLOCK_DOKUMENTACIJA_ITEMS_ACTION,
      LockUnlockDokumentacijaListItemsActionComponent
    ],
    [EarhivUiComponents.NEW_KLASA_ACTION, NewKlasaActionComponent],
    [
      EarhivUiComponents.OPEN_KONVERZIJA_POJAVNI_OBLIK_ACTION,
      OpenKonverzijaPojavniOblikActionComponent
    ],
    [
      EarhivUiComponents.KONVERTIRAJ_POJAVNI_OBLIK_ACTION,
      KonvertirajPojavniOblikActionComponent
    ],
    [
      EarhivUiComponents.OPEN_GENERATOR_URBROJ_ACTION,
      OpenGeneratorUrbrojActionComponent
    ]
  ]);

  constructor(private componentService: UIComponentService) {
    this.initUIComponents();
  }

  initUIComponents() {
    const compIdByControlTypeMap = InputUtils.compIdByControlType as any;
    compIdByControlTypeMap[EarhivInputControlTypes.EDITABLE_ITEMLIST] =
      EarhivUiComponents.EDITABLE_ITEMLIST_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.ARHIVSKA_OZNAKA] =
      EarhivUiComponents.ARHIVSKA_OZNAKA_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.GEONAMES_INTEGRATION] =
      EarhivUiComponents.GEONAMES_INTEGRATION_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.VIAF_INTEGRATION] =
      EarhivUiComponents.VIAF_INTEGRATION_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.RANGE_KOMPLEKSNI_INPUT] =
      EarhivUiComponents.VRIJEME_RANGE_KOMPLEKSNI_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.VRIJEME_KOMPLEKSNI_INPUT] =
      EarhivUiComponents.VRIJEME_KOMPLEKSNI_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.LAZY_DROPDOWN] =
      EarhivUiComponents.LAZY_DROPDOWN_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.LOCATION_FROM_STORAGE] =
      EarhivUiComponents.LOCATION_FROM_STORAGE;
    compIdByControlTypeMap[
      EarhivInputControlTypes.DEDUPLICATION_AGENT_DROPDOWN
    ] = EarhivUiComponents.DEDUPLICATION_AGENT_DROPDOWN;
    compIdByControlTypeMap[EarhivInputControlTypes.POJAVNI_OBLIK_NAZIV_INPUT] =
      EarhivUiComponents.POJAVNI_OBLIK_NAZIV_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.ROK_CUVANJA_TREE_INPUT] =
      EarhivUiComponents.ROK_CUVANJA_TREE_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.ARHIVSKA_KLASIFIKACIJA] =
      EarhivUiComponents.ARHIVSKA_KLASIFIKACIJA;
    compIdByControlTypeMap[EarhivInputControlTypes.BASIC_FILE_INPUT] =
      EarhivUiComponents.BASIC_FILE_INPUT;
    compIdByControlTypeMap[
      EarhivInputControlTypes.KLASIFIKACIJSKI_SIFRARNIK_INPUT
    ] = EarhivUiComponents.KLASIFIKACIJSKI_SIFRARNIK_INPUT;
    compIdByControlTypeMap[EarhivInputControlTypes.UPRAVNA_STVAR_INPUT] =
      EarhivUiComponents.UPRAVNA_STVAR_INPUT;
    compIdByControlTypeMap[
      EarhivInputControlTypes.ROK_CUVANJA_HIERARCHY_DROPDOWN
    ] = EarhivUiComponents.ROK_CUVANJA_HIERARCHY_DROPDOWN;
    compIdByControlTypeMap[
      EarhivInputControlTypes.JEDINICA_GRADIVA_INHERITABLE_ATTRIBUTE_INPUT
    ] = EarhivUiComponents.JEDINICA_GRADIVA_INHERITABLE_ATTRIBUTE_INPUT;
    compIdByControlTypeMap[
      EarhivInputControlTypes.POJAVNI_OBLIK_INHERITABLE_ATTRIBUTE_INPUT
    ] = EarhivUiComponents.POJAVNI_OBLIK_INHERITABLE_ATTRIBUTE_INPUT;
    compIdByControlTypeMap[
      EarhivInputControlTypes.DEDUPLICATION_MJESTO_DROPDOWN
    ] = EarhivUiComponents.DEDUPLICATION_MJESTO_DROPDOWN;

    this.CUSTOM_REGISTRY.forEach((compType: Type<any>, compId: string) => {
      this.componentService.registerType(compId, compType);
    });
    SharedModule.getComponentRegistry().forEach(
      (compType: Type<any>, compId: string) => {
        this.componentService.registerType(compId, compType);
      }
    );
  }
}
